import * as React from "react";
import { createContext, useContext, useState, ReactNode } from "react";
import { Link } from "react-router-dom";
import { Slot } from "@radix-ui/react-slot";
import { ChevronRight, MoreHorizontal } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ProtectedNavLink } from "@/components/ProtectedNavigationLink";
import { RouteId } from "@/routes/getRoute";

import { cn } from "@/lib/utils";

interface BreadcrumbContextType {
  dynamicTitle: string | null;
  setDynamicTitle: (title: string | null) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextType | undefined>(
  undefined,
);

export const BreadcrumbProvider = ({ children }: { children: ReactNode }) => {
  const [dynamicTitle, setDynamicTitle] = useState<string | null>(null);

  return (
    <BreadcrumbContext.Provider value={{ dynamicTitle, setDynamicTitle }}>
      {children}
    </BreadcrumbContext.Provider>
  );
};

export const useBreadcrumb = () => {
  const context = useContext(BreadcrumbContext);
  if (context === undefined) {
    throw new Error("useBreadcrumb must be used within a BreadcrumbProvider");
  }
  return context;
};

function Breadcrumb({ ...props }: React.ComponentProps<"nav">) {
  const { t } = useTranslation();
  return (
    <nav
      aria-label={t("breadcrumbs.breadcrumb")}
      data-slot="breadcrumb"
      {...props}
    />
  );
}

function BreadcrumbList({ className, ...props }: React.ComponentProps<"ol">) {
  return (
    <ol
      data-slot="breadcrumb-list"
      className={cn(
        "flex flex-wrap items-center gap-1.5 text-sm break-words text-muted-foreground sm:gap-2.5",
        className,
      )}
      {...props}
    />
  );
}

function BreadcrumbItem({ className, ...props }: React.ComponentProps<"li">) {
  return (
    <li
      data-slot="breadcrumb-item"
      className={cn("inline-flex items-center gap-1.5", className)}
      {...props}
    />
  );
}

function BreadcrumbLink({
  asChild,
  className,
  to,
  routeId,
  params,
  ...props
}: (React.ComponentProps<"a"> | React.ComponentProps<typeof Link>) & {
  asChild?: boolean;
  to?: string;
  routeId?: RouteId;
  params?: Record<string, string>;
}) {
  const Comp = asChild ? Slot : Link;

  // If we have a routeId, use ProtectedNavLink for feature flag checking
  if (routeId) {
    return (
      <ProtectedNavLink
        route={routeId}
        params={params || {}}
        className={cn("transition-colors hover:text-foreground", className)}
        {...props}
      />
    );
  }

  // Use the fallback "to" prop
  return (
    <Comp
      data-slot="breadcrumb-link"
      className={cn("transition-colors hover:text-foreground", className)}
      to={to || "#"}
      {...props}
    />
  );
}

function BreadcrumbPage({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="breadcrumb-page"
      role="link"
      aria-disabled="true"
      aria-current="page"
      className={cn("font-normal text-foreground", className)}
      {...props}
    />
  );
}

function BreadcrumbSeparator({
  children,
  className,
  ...props
}: React.ComponentProps<"li">) {
  return (
    <li
      data-slot="breadcrumb-separator"
      role="presentation"
      aria-hidden="true"
      className={cn("[&>svg]:size-3.5", className)}
      {...props}
    >
      {children ?? <ChevronRight />}
    </li>
  );
}

function BreadcrumbEllipsis({
  className,
  ...props
}: React.ComponentProps<"span">) {
  const { t } = useTranslation();
  return (
    <span
      data-slot="breadcrumb-ellipsis"
      role="presentation"
      aria-hidden="true"
      className={cn("flex size-9 items-center justify-center", className)}
      {...props}
    >
      <MoreHorizontal className="size-4" />
      <span className="sr-only">{t("global.more")}</span>
    </span>
  );
}

export {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
};
